import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle, Pencil } from "lucide-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { getFinancialTransactions, createFinancialTransaction, updateFinancialTransaction, deleteFinancialTransaction } from "@/lib/api-financial";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { formatBRL } from "@/lib/utils";
import { resolveFinancialCategoryLabel, useFinancialCategories } from "@/hooks/useFinancialCategories";

const statusVariants: Record<string, string> = { pending: "secondary", confirmed: "default", paid: "default", cancelled: "destructive" };

export default function ProducerAccountsPayable({ producerId }: { producerId: string }) {
  const { t, i18n } = useTranslation("producerFinancial");
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ description: "", amount: "", category: "commission", due_date: "", notes: "" });
  const [editId, setEditId] = useState<string>("");
  const [editForm, setEditForm] = useState({ description: "", amount: "", category: "commission", due_date: "", notes: "", status: "pending" });
  const { categories, addCategory, removeCategory } = useFinancialCategories(producerId, "payable");

  useEffect(() => {
    if (!categories.some((category) => category.value === form.category)) {
      setForm((previous) => ({ ...previous, category: categories[0]?.value || "commission" }));
    }
  }, [categories, form.category]);

  useEffect(() => {
    if (!categories.some((category) => category.value === editForm.category)) {
      setEditForm((previous) => ({ ...previous, category: categories[0]?.value || "commission" }));
    }
  }, [categories, editForm.category]);

  const categoryOptionsForCreate = useMemo(() => {
    if (categories.some((category) => category.value === form.category)) return categories;
    return [...categories, { value: form.category, label: resolveFinancialCategoryLabel(form.category, categories) }];
  }, [categories, form.category]);

  const categoryOptionsForEdit = useMemo(() => {
    if (categories.some((category) => category.value === editForm.category)) return categories;
    return [...categories, { value: editForm.category, label: resolveFinancialCategoryLabel(editForm.category, categories) }];
  }, [categories, editForm.category]);

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["financial-payable", producerId, statusFilter],
    queryFn: () => getFinancialTransactions(producerId, {
      type: "payable",
      status: statusFilter === "all" ? undefined : statusFilter,
    }),
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: () => createFinancialTransaction({
      producer_id: producerId,
      type: "payable",
      category: form.category,
      description: form.description,
      amount: parseFloat(form.amount) || 0,
      due_date: form.due_date || undefined,
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-payable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      setShowCreate(false);
      setForm({ description: "", amount: "", category: "commission", due_date: "", notes: "" });
      toast({ title: t("accountsPayable.toast.created") });
    },
    onError: (err: any) => toast({ title: t("accountsPayable.toast.error"), description: err.message, variant: "destructive" }),
  });

  const confirmMut = useMutation({
    mutationFn: (id: string) => updateFinancialTransaction(id, { status: "paid", paid_at: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-payable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      toast({ title: t("accountsPayable.toast.paid") });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFinancialTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-payable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      toast({ title: t("accountsPayable.toast.deleted") });
    },
  });

  const editMut = useMutation({
    mutationFn: () => updateFinancialTransaction(editId, {
      description: editForm.description,
      amount: parseFloat(editForm.amount) || 0,
      category: editForm.category,
      due_date: editForm.due_date || null,
      notes: editForm.notes || null,
      status: editForm.status,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-payable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      setShowEdit(false);
      setEditId("");
      toast({ title: t("accountsPayable.toast.updated") });
    },
    onError: (err: any) => toast({ title: t("accountsPayable.toast.error"), description: err.message, variant: "destructive" }),
  });

  const openEditDialog = (row: any) => {
    setEditId(row.id);
    setEditForm({
      description: row.description || "",
      amount: String(row.amount ?? ""),
      category: row.category || "commission",
      due_date: row.due_date ? String(row.due_date).slice(0, 10) : "",
      notes: row.notes || "",
      status: row.status || "pending",
    });
    setShowEdit(true);
  };

  const columns: DataTableColumn<any>[] = [
    { key: "description", header: t("accountsPayable.table.description"), render: (row) => <span className="font-medium">{row.description}</span> },
    { key: "category", header: t("accountsPayable.table.category"), render: (row) => <span className="text-sm">{resolveFinancialCategoryLabel(row.category, categories)}</span> },
    {
      key: "notes",
      header: t("accountsPayable.table.notes"),
      render: (row) => (
        <span className="text-sm text-muted-foreground line-clamp-2">
          {row.notes || "—"}
        </span>
      ),
      className: "max-w-[220px]",
    },
    { key: "amount", header: t("accountsPayable.table.amount"), render: (row) => <span className="font-semibold text-foreground">{formatBRL(row.amount)}</span> },
    { key: "due_date", header: t("accountsPayable.table.dueDate"), render: (row) => row.due_date ? format(new Date(row.due_date), "dd/MM/yyyy") : "—" },
    { key: "status", header: t("accountsPayable.table.status"), render: (row) => <Badge variant={statusVariants[row.status] as any}>{t(`accountsPayable.status.${row.status}`, { defaultValue: row.status })}</Badge> },
    {
      key: "actions", header: "", render: (row) => (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={() => openEditDialog(row)} title={t("accountsPayable.actions.editTooltip")}>
            <Pencil className="h-4 w-4" />
          </Button>
          {row.status === "pending" && (
            <Button size="icon" variant="ghost" onClick={() => confirmMut.mutate(row.id)} title={t("accountsPayable.actions.confirmTooltip")}>
              <CheckCircle className="h-4 w-4 text-primary" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => deleteMut.mutate(row.id)} title={t("accountsPayable.actions.delete")}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const total = transactions.reduce((s: number, t: any) => s + Number(t.amount), 0);

  const handleAddCategory = async () => {
    const result = await addCategory(newCategoryLabel);
    if (!result.ok) {
      toast({ title: t("accountsPayable.toast.invalidCategory"), description: result.reason, variant: "destructive" });
      return;
    }

    setNewCategoryLabel("");
    setForm((previous) => ({ ...previous, category: result.value }));
    setEditForm((previous) => ({ ...previous, category: result.value }));
    toast({ title: t("accountsPayable.toast.categoryAdded") });
  };

  const handleRemoveCategory = async (value: string) => {
    const result = await removeCategory(value);
    if (!result.ok) {
      toast({ title: t("accountsPayable.toast.cannotRemoveCategory"), description: result.reason, variant: "destructive" });
      return;
    }

    const fallback = result.fallback || "commission";
    if (form.category === value) {
      setForm((previous) => ({ ...previous, category: fallback }));
    }
    if (editForm.category === value) {
      setEditForm((previous) => ({ ...previous, category: fallback }));
    }

    toast({ title: t("accountsPayable.toast.categoryRemoved") });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("accountsPayable.statusFilter.all")}</SelectItem>
              <SelectItem value="pending">{t("accountsPayable.statusFilter.pending")}</SelectItem>
              <SelectItem value="confirmed">{t("accountsPayable.statusFilter.confirmed")}</SelectItem>
              <SelectItem value="paid">{t("accountsPayable.statusFilter.paid")}</SelectItem>
              <SelectItem value="cancelled">{t("accountsPayable.statusFilter.cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{t("accountsPayable.total")} <strong className="text-primary">{formatBRL(total)}</strong></span>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={() => setShowCategoryManager(true)}>{t("accountsPayable.manageCategories")}</Button>
          <Button size="sm" className="flex-1 sm:flex-none" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-1" /> {t("accountsPayable.newPayable")}</Button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable columns={columns} data={transactions} isLoading={isLoading} emptyMessage={t("accountsPayable.empty")} />
      </div>

      {/* Mobile Card List View */}
      <div className="md:hidden space-y-2.5">
        {isLoading ? (
          <div className="p-4 text-center text-xs text-muted-foreground">{t("accountsPayable.loading")}</div>
        ) : transactions.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground border border-dashed border-border/40 rounded-2xl">
            {t("accountsPayable.empty")}
          </div>
        ) : (
          transactions.map((tx: any) => (
            <div key={tx.id} className="rounded-2xl border border-border/40 bg-card p-4 space-y-2.5 shadow-none">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-xs font-semibold text-foreground">{tx.description}</h4>
                  <p className="text-[10px] text-muted-foreground">
                    {resolveFinancialCategoryLabel(tx.category, categories)} {tx.due_date ? `· ${t("accountsPayable.table.dueDateShort")} ${format(new Date(tx.due_date), "dd/MM/yyyy")}` : ""}
                  </p>
                </div>
                <Badge variant={statusVariants[tx.status] as any} className="text-[10px] shrink-0 font-normal">
                  {t(`accountsPayable.status.${tx.status}`, { defaultValue: tx.status })}
                </Badge>
              </div>

              {tx.notes && (
                <p className="text-[11px] text-muted-foreground/80 line-clamp-2 bg-muted/20 p-2 rounded-xl">
                  {tx.notes}
                </p>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-border/30 text-xs">
                <span className="text-sm font-semibold text-foreground tabular-nums">{formatBRL(tx.amount)}</span>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditDialog(tx)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {tx.status === "pending" && (
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-primary font-medium" onClick={() => confirmMut.mutate(tx.id)}>
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> {t("accountsPayable.actions.markPaid")}
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => deleteMut.mutate(tx.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("accountsPayable.create.title")}</DialogTitle>
            <DialogDescription>{t("accountsPayable.create.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("accountsPayable.create.descriptionLabel")}</Label><Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t("accountsPayable.create.descriptionPlaceholder")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("accountsPayable.create.amountLabel")}</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div>
                <Label>{t("accountsPayable.create.categoryLabel")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptionsForCreate.map((category) => (
                      <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>{t("accountsPayable.create.dueDateLabel")}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            <div><Label>{t("accountsPayable.create.notesLabel")}</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("accountsPayable.create.cancel")}</Button>
            <Button onClick={() => createMut.mutate()} disabled={!form.description || !form.amount || createMut.isPending}>{t("accountsPayable.create.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("accountsPayable.edit.title")}</DialogTitle>
            <DialogDescription>{t("accountsPayable.edit.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("accountsPayable.create.descriptionLabel")}</Label><Input value={editForm.description} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("accountsPayable.create.amountLabel")}</Label><Input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div>
                <Label>{t("accountsPayable.create.categoryLabel")}</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptionsForEdit.map((category) => (
                      <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("accountsPayable.create.dueDateLabel")}</Label><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm(p => ({ ...p, due_date: e.target.value }))} /></div>
              <div>
                <Label>{t("accountsPayable.edit.statusLabel")}</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t("accountsPayable.status.pending")}</SelectItem>
                    <SelectItem value="confirmed">{t("accountsPayable.status.confirmed")}</SelectItem>
                    <SelectItem value="paid">{t("accountsPayable.status.paid")}</SelectItem>
                    <SelectItem value="cancelled">{t("accountsPayable.status.cancelled")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("accountsPayable.create.notesLabel")}</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>{t("accountsPayable.edit.cancel")}</Button>
            <Button onClick={() => editMut.mutate()} disabled={!editForm.description || !editForm.amount || editMut.isPending}>{t("accountsPayable.edit.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("accountsPayable.categoryManager.title")}</DialogTitle>
            <DialogDescription>{t("accountsPayable.categoryManager.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newCategoryLabel}
                onChange={(e) => setNewCategoryLabel(e.target.value)}
                placeholder={t("accountsPayable.categoryManager.inputPlaceholder")}
              />
              <Button onClick={handleAddCategory} disabled={!newCategoryLabel.trim()}>{t("accountsPayable.categoryManager.add")}</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {categories.map((category) => (
                <div key={category.value} className="flex items-center justify-between rounded-md border p-2">
                  <span className="text-sm">{category.label}</span>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleRemoveCategory(category.value)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryManager(false)}>{t("accountsPayable.categoryManager.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
